import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';

// Theme switching. Effective theme = manual preference || OS preference.
// Writes to `<html data-theme="dark">` so the [data-theme="dark"] block
// in tokens.css activates. Boot-time pre-mount apply lives in main.ts
// so the first paint already has the right theme (no light flash on
// dark-mode users).

const STORAGE_KEY = 'hq.ui.theme';

export type ThemePreference = 'system' | 'light' | 'dark';

function readManual(): ThemePreference {
  if (typeof localStorage === 'undefined') return 'system';
  const raw = localStorage.getItem(STORAGE_KEY);
  if (raw === 'light' || raw === 'dark' || raw === 'system') return raw;
  return 'system';
}

function writeManual(value: ThemePreference) {
  if (typeof localStorage === 'undefined') return;
  if (value === 'system') localStorage.removeItem(STORAGE_KEY);
  else localStorage.setItem(STORAGE_KEY, value);
}

function osPrefersDark(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

// Keep the mobile browser chrome (status bar / address bar) matched to the
// page background, so iOS Safari doesn't sample the page for it and drift.
// We read the resolved <html> background (an rgb() string the chrome parser
// accepts) rather than the raw oklch token, which older mobile browsers
// won't parse in a theme-color meta.
function applyThemeColor() {
  if (typeof document === 'undefined') return;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (!meta) return;
  const bg = getComputedStyle(document.documentElement).backgroundColor;
  if (bg && bg !== 'rgba(0, 0, 0, 0)') meta.setAttribute('content', bg);
}

function apply(theme: 'light' | 'dark') {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset.theme = theme;
  // Bridge markstream-vue's theming to ours. The library flips its base --ms-*
  // palette on a `.dark` class (it doesn't read our data-theme attribute), so we
  // mirror the switch here. This drives the library's own (already-monochrome)
  // light/dark ramp for every rendered component - tables, code, inline code,
  // blockquotes, admonitions - in one place. (The Shiki syntax theme + mermaid
  // also need the renderer's isDark prop, which Markdown.vue binds from this store.)
  document.documentElement.classList.toggle('dark', theme === 'dark');
  // Re-read AFTER data-theme flips so the chrome matches the new canvas.
  applyThemeColor();
}

// Bootstrap helper - call from main.ts BEFORE app mount so the first
// paint already has the right theme.
export function bootstrapTheme() {
  const manual = readManual();
  const effective = manual === 'system' ? (osPrefersDark() ? 'dark' : 'light') : manual;
  apply(effective);
}

export const useThemeStore = defineStore('theme', () => {
  const manual = ref<ThemePreference>(readManual());
  const osDark = ref<boolean>(osPrefersDark());

  if (typeof window !== 'undefined') {
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    mql.addEventListener('change', (e) => { osDark.value = e.matches; });
  }

  const effective = computed<'light' | 'dark'>(() =>
    manual.value === 'system' ? (osDark.value ? 'dark' : 'light') : manual.value,
  );

  // Apply on any change. The boot-time apply in main.ts handles the
  // first paint; this watch handles user toggles after mount.
  watch(effective, (v) => apply(v), { immediate: true });
  watch(manual, (v) => writeManual(v));

  function setManual(v: ThemePreference) { manual.value = v; }
  function toggle() { manual.value = effective.value === 'dark' ? 'light' : 'dark'; }

  return { manual, osDark, effective, setManual, toggle };
});
